export function isReadonlyMode() {
    return window.IS_READONLY === true;
}

export function isProductSupportMode() {
    return window.PRODUCT_SUPPORT_MODE === true;
}

export function isProductSupportReadonlyMode() {
    return window.IS_READONLY === true && window.PRODUCT_SUPPORT_MODE === true;
}

export function isProduction() {
    return window.location.host.includes('connect.garmin');
}

export function isAccountSharingReadonlyMode() {
    return window.IS_READONLY === true && window.PRODUCT_SUPPORT_MODE !== true;
}

export function isRaceManagementMode() {
    return window.IS_RACE_MANAGEMENT === true;
}

const FeatureActionBitLocation = {
    View: 0,
    Edit: 1
};

/**
 * @typedef {"View" | "Edit"} FeaturePermission
 * */

/**
 * Check if user has View / Edit / Delete access to feature
 * @param {*} feature key of FEATURES_ACCESS
 * @param {FeaturePermission} action
 * @returns {boolean|null} user has access or feature is not available
 */
export function hasAccess(feature, action = 'View') {
    const featuresAccess = window.FEATURES_ACCESS;

    // Normal user, has access to all features
    if (featuresAccess === undefined) {
        return true;
    }

    const featureAccess = featuresAccess[feature];

    if (featureAccess === undefined) {
        // Not available, 404
        return null;
    }

    try {
        return checkBit(featureAccess, FeatureActionBitLocation[action]);
    } catch (error) {
        console.error(error);
        return false;
    }
}

/**
 * Give component a feature, provide canView and canEdit props.
 * Will not prevent component from rendering.
 * @example:
 * export default withFeature(MCT)(withTranslation([])(Leaderboard));
 *
 * @param {'connect-app-common/feature'} feature
 */
export function withFeature(feature) {
    const canView = hasAccess(feature);
    const canEdit = hasAccess(feature, 'Edit');
    return function(Component) {
        return function(props) {
            return <Component {...props} canView={canView} canEdit={canEdit} />;
        };
    };
}

/**
 * Give component a feature and check if user has view permission.
 * Provide canView and canEdit props.
 * Prevent component from rendering if user has no view permission.
 * Can take optional no access component.
 * @example:
 * function OptionalNoAccess() {
 *   return <>Custom No Access</>
 * }
 * export default withViewFeature(MCT)(withTranslation([])(Leaderboard), OptionalNoAccess);
 *
 * @param {'connect-app-common/feature'} feature
 */
export function withViewFeature(feature) {
    const canView = hasAccess(feature);
    if (!canView) {
        return (Component, NoAccess) => (props) => (NoAccess ? <NoAccess {...props} /> : null);
    }
    const canEdit = hasAccess(feature, 'Edit');
    return function(Component) {
        return function(props) {
            return <Component {...props} canView={canView} canEdit={canEdit} />;
        };
    };
}

/**
 * Give component a feature and check if user has edit permission.
 * Provide canView and canEdit props.
 * Prevent component from rendering if user has no edit permission.
 * Can take optional no access component.
 * @example:
 * function OptionalNoAccess() {
 *   return <>Custom No Access</>
 * }
 * export default withEditFeature(MCT)(withTranslation([])(Leaderboard), OptionalNoAccess);
 * @param {'connect-app-common/feature'} feature
 */
export function withEditFeature(feature) {
    const canEdit = hasAccess(feature, 'Edit');
    if (!canEdit) {
        return (Component, NoAccess) => (props) => (NoAccess ? <NoAccess {...props} /> : null);
    }
    return function(Component) {
        return function(props) {
            return <Component {...props} canView={true} canEdit={canEdit} />;
        };
    };
}

/**
 * Util function to check if GCW is launched by Harmony Connect App, which would inject the predefined object into window on launching the webview
 * @returns {boolean}
 */
export function isLaunchByHarmonyConnectApp() {
    // Currently it's expected to be an empty object, and might be extended in the future
    return !!window.LAUNCH_BY_HARMONY_CONNECT_APP;
}

export function hasEditRouteAccess(feature) {
    const access = hasAccess(feature, 'Edit');

    switch (access) {
        case false:
            return () => Window.App.displayPageNotSupportedPage(feature);
        case null:
            return () => Window.App.displayPageNotFoundPage(); // Not found
    }
}

export function blockInReadOnly() {
    if (isReadonlyMode()) {
        return () => Window.App.displayPageNotFoundPage();
    }
}

/**
 * Control route access base on FEATURES_ACCESS
 * @param {*} feature key of FEATURES_ACCESS
 * @param {FeaturePermission} permission
 */
export function routeAccess(feature, permission = 'View') {
    const access = hasAccess(feature, permission);

    switch (access) {
        case null:
            return () => Window.App.displayPageNotFoundPage(); // Not found
        case false:
            return () => Window.App.displayPageNotSupportedPage(feature);
    }
}

export function blockInAccountSharingReadonly() {
    if (isAccountSharingReadonlyMode()) {
        return () => Window.App.displayPageNotFoundPage();
    }
}

export function blockInProductSupportReadonly() {
    if (isProductSupportReadonlyMode()) {
        return () => Window.App.displayPageNotFoundPage();
    }
}

// This is the Middleware for redux store
// Actions on this list will be ignored
// - No group in Account Sharing / read-only mode
export const accountSharingFilterMiddleware = (storeAPI) => (next) => (action) => {
    switch (action.type) {
        case 'REQUEST_CALENDAR_WEEK':
        case 'REQUEST_CALENDAR_MONTH':
            if (action.groupId) {
                return;
            }
        default:
            return next(action);
    }
};

/**
 * Check if decimal number at selected position is on or off
 * @param {number} decimalNumber decimal number
 * @param {number} position
 * @returns bool
 */
function checkBit(decimalNumber, position) {
    if (typeof decimalNumber === 'undefined' || typeof position === 'undefined') {
        throw Error('number and position are required');
    }
    const mask = 1 << position;
    return (decimalNumber & mask) !== 0;
}
